import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const EmbedGeneratorPage = () => {
    const { t } = useTranslation();
    const [text, setText] = useState("Guten Tag, wie geht es Ihnen?");
    const [language, setLanguage] = useState("de-DE");
    // Base URL is determined by the current window location (or hardcoded for now)
    const baseUrl =
        typeof window !== "undefined" ? window.location.origin : "https://aussprachetrainer.org";
    const [snippet, setSnippet] = useState("");
    const [copied, setCopied] = useState(false);

    const languages = [
        { code: "de-DE", name: "German (Germany)" },
        { code: "en-GB", name: "English (UK)" },
        { code: "fr-FR", name: "French (France)" },
    ];

    useEffect(() => {
        // Update snippet whenever inputs change
        const encodedText = encodeURIComponent(text);
        const url = `${baseUrl}/embed?text=${encodedText}&lang=${language}`;

        const newSnippet = `<iframe
    src="${url}"
    frameborder="0"
    allow="microphone"
    allowfullscreen
    style="display: block; width: 100%; max-width: 800px; height: 360px; border: 1px solid purple; border-radius: 4px; margin: 0 auto;"
    onload="(function(){var w=window.innerWidth;var i=this;if(w>=1025){i.style.height='450px';}else if(w>=768){i.style.height='400px';}}).call(this)"
></iframe>`;

        setSnippet(newSnippet);
        setCopied(false);
    }, [text, language, baseUrl]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(snippet);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy:", err);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12">
                    <h1 className="text-3xl font-extrabold text-foreground sm:text-4xl">
                        {t("embedGenerator.title")}
                    </h1>
                    <p className="mt-4 text-lg text-gray-500 max-w-2xl mx-auto">
                        {t("embedGenerator.description")}
                    </p>
                </div>

                <div className="bg-secondary shadow-md rounded-xl p-6 mb-8 border border-border">
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <div className="sm:col-span-6">
                            <label
                                htmlFor="text"
                                className="block text-sm font-medium text-foreground mb-2"
                            >
                                {t("embedGenerator.textToPractice")}
                            </label>
                            <div className="mt-1">
                                <textarea
                                    id="text"
                                    name="text"
                                    rows={3}
                                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border border-border rounded-lg p-3 bg-white"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={t("embedGenerator.textPlaceholder")}
                                />
                            </div>
                        </div>

                        <div className="sm:col-span-3">
                            <label
                                htmlFor="language"
                                className="block text-sm font-medium text-foreground mb-2"
                            >
                                {t("embedGenerator.language")}
                            </label>
                            <div className="mt-1">
                                <select
                                    id="language"
                                    name="language"
                                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border border-border rounded-lg p-3 bg-white"
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                >
                                    {languages.map((lang) => (
                                        <option key={lang.code} value={lang.code}>
                                            {lang.name} ({lang.code})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-secondary shadow-md rounded-xl p-6 mb-8 border border-border">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-medium text-foreground">
                            {t("embedGenerator.generatedSnippet")}
                        </h2>
                        <button
                            onClick={handleCopy}
                            className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                                copied ? "opacity-75 cursor-default" : ""
                            }`}
                        >
                            {copied ? (
                                <>
                                    <svg
                                        className="w-5 h-5 mr-2 text-green-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                    {t("embedGenerator.copied")}
                                </>
                            ) : (
                                <>
                                    <svg
                                        className="w-5 h-5 mr-2 text-gray-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                                        />
                                    </svg>
                                    {t("embedGenerator.copySnippet")}
                                </>
                            )}
                        </button>
                    </div>
                    <div className="relative">
                        <pre className="bg-gray-100 rounded-lg p-4 overflow-x-auto border border-gray-300">
                            <code className="text-gray-800 text-sm font-mono whitespace-pre-wrap">
                                {snippet}
                            </code>
                        </pre>
                    </div>
                </div>

                <div className="bg-secondary shadow-md rounded-xl p-6 border border-border">
                    <h2 className="text-lg font-medium text-foreground mb-4">
                        {t("embedGenerator.preview")}
                    </h2>
                    <div className="border border-border rounded-lg p-4 bg-gray-50 flex justify-center min-h-[400px] items-center">
                        <div className="w-full" dangerouslySetInnerHTML={{ __html: snippet }} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmbedGeneratorPage;
